import { CircleCheck, Lock } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import type { VisitorForm, VisitorSubmit } from '@escala/contracts';
import { BrandMark, pageTitle } from '@/components/Brand';
import OrgMark from '@/components/OrgMark';
import { LegalLink } from '@/components/Privacy';
import { Card } from '@/design-system/patterns/Card';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import { Input } from '@/design-system/ui/Input';
import { SimpleSelect } from '@/design-system/ui/Select';
import { todayISO } from '@escala/domain/dates';
import { NATAL_DISTRICTS } from '@escala/domain/geography';
import { formatPhone } from '@escala/domain/members';
import type { AgeRange, VisitorSource } from '@escala/domain/types';
import {
  AGE_RANGES,
  VISITOR_CONSENT_TEXT,
  VISITOR_SOURCES,
  emptyVisitorDraft,
  visitorErrors,
} from '@escala/domain/visitors';

import { authErrorMessage } from './authError';
import { Notice } from './PublicRegistration';

/** De onde a ficha lê a igreja e para onde envia (a API pública; nos testes, uma fonte falsa). */
export interface VisitorFormSource {
  load: () => Promise<VisitorForm>;
  submit: (body: VisitorSubmit) => Promise<void>;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; form: VisitorForm }
  | { kind: 'done'; form: VisitorForm; firstName: string };

const EMPTY: Omit<VisitorSubmit, 'adult' | 'consent'> = {
  name: '',
  phone: '',
  email: '',
  ageRange: 'unknown',
  district: '',
  source: 'invite',
  firstTime: true,
  wantsContact: true,
  wantsMembership: false,
};

/**
 * Ficha de visitante pelo link (ou QR) da igreja (`/{igreja}/visitante/{token}`): uma
 * etapa só, coleta mínima, só para quem tem 18 anos ou mais e com o consentimento da LGPD.
 * O mesmo celular soma a visita na ficha que já existe.
 */
export default function PublicVisitor({
  source,
  closeLabel = 'Voltar',
  onClose,
}: {
  source: VisitorFormSource;
  closeLabel?: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    document.title = pageTitle('Ficha de visitante');

    let active = true;

    source
      .load()
      .then((form) => active && setPhase({ kind: 'ready', form }))
      .catch((error: unknown) => {
        if (!active) return;
        setPhase(
          error && typeof error === 'object' && 'status' in error && error.status === 404
            ? { kind: 'missing' }
            : { kind: 'failed' }
        );
      });

    return () => {
      active = false;
    };
  }, [source]);

  const form = phase.kind === 'ready' || phase.kind === 'done' ? phase.form : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Ficha de visitante"
      className="fixed inset-0 z-50 overflow-y-auto bg-page text-ink"
    >
      <div className="mx-auto flex max-w-150 flex-col gap-5 px-4 pt-7 pb-12">
        <header className="flex items-center gap-2.5">
          <BrandMark />
          <span className="flex-1" />
          {form ? (
            <span className="flex h-9.5 min-w-0 items-center gap-2 rounded-control border border-line-input bg-white pr-3 pl-1.5">
              <OrgMark org={form.org} size="sm" />
              <span className="max-w-55 truncate text-small font-semibold max-sm:max-w-30">
                {form.org.name}
              </span>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {closeLabel}
          </Button>
        </header>

        {phase.kind === 'loading' ? (
          <Card className="p-10 text-center text-ink-subtle" role="status">
            Abrindo a ficha…
          </Card>
        ) : null}
        {phase.kind === 'missing' ? (
          <Notice icon={<Lock className="size-6 stroke-[1.8]" />} tone="warning" title="Link inválido">
            Este link não existe mais — talvez a igreja tenha gerado um novo. Peça o link atualizado na
            recepção.
          </Notice>
        ) : null}
        {phase.kind === 'failed' ? (
          <Notice
            icon={<Lock className="size-6 stroke-[1.8]" />}
            tone="warning"
            title="Não foi possível abrir"
          >
            Confira a internet e tente de novo em instantes.
          </Notice>
        ) : null}
        {phase.kind === 'ready' && !phase.form.open ? (
          <Notice
            icon={<Lock className="size-6 stroke-[1.8]" />}
            tone="warning"
            title="Fichas fechadas no momento"
          >
            Este link não está recebendo fichas agora. Fale com a recepção da igreja.
          </Notice>
        ) : null}
        {phase.kind === 'done' ? (
          <Notice
            icon={<CircleCheck className="size-7 stroke-[1.8]" />}
            tone="success"
            title={`Obrigado, ${phase.firstName}!`}
            actions={
              <Button
                size="lg"
                variant="secondary"
                onClick={() => setPhase({ kind: 'ready', form: phase.form })}
              >
                Enviar outra ficha
              </Button>
            }
          >
            Foi muito bom ter você com a gente. Se pediu contato, alguém da {phase.form.org.name} vai falar
            com você em breve.
          </Notice>
        ) : null}
        {phase.kind === 'ready' && phase.form.open ? (
          <VisitorFields
            orgName={phase.form.org.name}
            onSubmit={async (body) => {
              await source.submit(body);
              setPhase({ kind: 'done', form: phase.form, firstName: body.name.trim().split(/\s+/)[0] ?? '' });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function VisitorFields({
  orgName,
  onSubmit,
}: {
  orgName: string;
  onSubmit: (body: VisitorSubmit) => Promise<void>;
}) {
  const [today] = useState(todayISO);
  const [data, setData] = useState(EMPTY);
  const [adult, setAdult] = useState(false);
  const [consent, setConsent] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();
  const draft = { ...emptyVisitorDraft(today), ...data, consent };
  const errors = showErrors ? visitorErrors(draft, today) : {};
  const set = (patch: Partial<typeof EMPTY>) => setData((d) => ({ ...d, ...patch }));

  const submit = async () => {
    setShowErrors(true);
    setSendError(null);

    if (Object.keys(visitorErrors(draft, today)).length || !adult) return;

    setBusy(true);
    try {
      await onSubmit({ ...data, adult: true, consent: true });
    } catch (error) {
      setSendError(authErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const message = (text?: string) =>
    text ? (
      <span role="alert" className="text-meta text-status-conflict">
        {text}
      </span>
    ) : null;

  return (
    <Card className="flex flex-col gap-4 rounded-dialog p-6 max-sm:p-4">
      <div>
        <h1 className="m-0 text-display font-extrabold tracking-[-0.02em]">Seja bem-vindo(a)!</h1>
        <p className="m-0 mt-1 text-pretty text-ink-muted">
          Deixe seu contato para a {orgName} receber você melhor. Leva menos de um minuto.
        </p>
      </div>

      <label className="flex flex-col gap-1.5" htmlFor={`${id}-name`}>
        <span className="text-small font-semibold">
          Seu nome<span className="text-status-conflict"> *</span>
        </span>
        <Input
          id={`${id}-name`}
          autoComplete="name"
          value={data.name}
          invalid={!!errors.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        {message(errors.name)}
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5" htmlFor={`${id}-phone`}>
          <span className="text-small font-semibold">Celular (WhatsApp)</span>
          <Input
            id={`${id}-phone`}
            inputMode="tel"
            autoComplete="tel-national"
            placeholder="(84) 99876-5432"
            value={formatPhone(data.phone)}
            invalid={!!errors.phone}
            onChange={(e) => set({ phone: e.target.value.replace(/\D/g, '').slice(0, 11) })}
          />
          {message(errors.phone)}
        </label>
        <label className="flex flex-col gap-1.5" htmlFor={`${id}-email`}>
          <span className="text-small font-semibold">E-mail</span>
          <Input
            id={`${id}-email`}
            type="email"
            autoComplete="email"
            value={data.email}
            invalid={!!errors.email}
            onChange={(e) => set({ email: e.target.value })}
          />
          {message(errors.email)}
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${id}-age`} className="text-small font-semibold">
            Faixa etária
          </label>
          <SimpleSelect
            id={`${id}-age`}
            value={data.ageRange}
            onValueChange={(v) => set({ ageRange: v as AgeRange })}
            options={AGE_RANGES}
          />
        </div>
        <label className="flex flex-col gap-1.5" htmlFor={`${id}-district`}>
          <span className="text-small font-semibold">Bairro</span>
          <Input
            id={`${id}-district`}
            list={`${id}-districts`}
            value={data.district}
            onChange={(e) => set({ district: e.target.value })}
          />
          <datalist id={`${id}-districts`}>
            {NATAL_DISTRICTS.map((d) => (
              <option key={d.name} value={d.name} />
            ))}
          </datalist>
        </label>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${id}-source`} className="text-small font-semibold">
          Como conheceu a igreja
        </label>
        <SimpleSelect
          id={`${id}-source`}
          value={data.source}
          onValueChange={(v) => set({ source: v as VisitorSource })}
          options={VISITOR_SOURCES}
        />
      </div>

      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1 text-small font-semibold">Sobre a sua visita</legend>
        {(
          [
            ['firstTime', 'É a minha primeira vez aqui'],
            ['wantsContact', 'Quero receber contato da igreja'],
            ['wantsMembership', 'Quero conhecer mais sobre ser membro'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="checkbox"
            aria-checked={data[key]}
            onClick={() => set({ [key]: !data[key] })}
            className="flex items-center gap-2 self-start text-small text-ink"
          >
            <CheckboxMark checked={data[key]} />
            {label}
          </button>
        ))}
      </fieldset>

      <div className="flex flex-col gap-2 rounded-block border border-line-card bg-surface-subtle p-3.5">
        <button
          type="button"
          role="checkbox"
          aria-checked={adult}
          onClick={() => setAdult((a) => !a)}
          className="flex items-center gap-2 text-left text-small font-semibold text-ink"
        >
          <CheckboxMark checked={adult} />
          Tenho 18 anos ou mais
        </button>
        {showErrors && !adult
          ? message(
              'A ficha pelo link é só para maiores de 18 anos. Crianças e adolescentes são bem-vindos com os responsáveis.'
            )
          : null}
        <button
          type="button"
          role="checkbox"
          aria-checked={consent}
          onClick={() => setConsent((c) => !c)}
          className="flex items-start gap-2 text-left text-small text-ink"
        >
          <CheckboxMark checked={consent} className="mt-0.5" />
          <span>{VISITOR_CONSENT_TEXT}</span>
        </button>
        <p className="m-0 text-meta text-ink-subtle">
          Leia a{' '}
          <LegalLink doc="privacy" orgName={orgName}>
            Política de Privacidade
          </LegalLink>{' '}
          e os{' '}
          <LegalLink doc="terms" orgName={orgName}>
            Termos de Uso
          </LegalLink>
          .
        </p>
        {message(errors.consent)}
      </div>

      {sendError ? <Alert tone="danger">{sendError}</Alert> : null}

      <Button size="lg" disabled={busy} onClick={() => void submit()}>
        {busy ? 'Enviando…' : 'Enviar ficha'}
      </Button>
    </Card>
  );
}
